import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineRecipe,
  defineSlotRecipe,
} from '@chakra-ui/react';
import { config as volyConfig } from '@voly/ui-components/src/v2/utils/chakra';

const pinpointConfig = defineConfig({
  theme: {
    tokens: {
      fonts: {
        heading: { value: 'var(--fontFamily)' },
        body: { value: 'var(--fontFamily)' },
      },
      colors: {
        primary: {
          50: { value: 'var(--primary50)' },
          75: { value: 'var(--primary75)' },
          100: { value: 'var(--primary100)' },
          200: { value: 'var(--primary200)' },
          300: { value: 'var(--primary300)' },
          400: { value: 'var(--primary400)' },
          500: { value: 'var(--primary500)' },
          600: { value: 'var(--primary600)' },
          700: { value: 'var(--primary700)' },
          800: { value: 'var(--primary800)' },
          900: { value: 'var(--primary900)' },
          950: { value: 'var(--primary950)' },
        },
      },
    },
    semanticTokens: {
      colors: {
        priorityHigh: {
          solid: { value: 'var(--priority-high)' },
        },
        priorityMedium: {
          solid: { value: 'var(--priority-medium)' },
        },
        priorityLow: {
          solid: { value: 'var(--priority-low)' },
        },
        primary: {
          solid: { value: 'var(--primary950)' },
          fg: { value: 'var(--button-primary-bg)' },
          contrast: { value: 'var(--primary-contrast)' },
          subtle: { value: 'var(--primary-subtle)' },
          muted: { value: 'var(--primary-muted)' },
          emphasized: { value: 'var(--primary-emphasized)' },
          focusRing: { value: 'var(--primary-focus-ring)' },
        },
        bg: {
          DEFAULT: { value: 'var(--bg)' },
          subtle: { value: 'var(--bg-subtle)' },
          muted: { value: 'var(--bg-muted)' },
          emphasized: { value: 'var(--bg-emphasized)' },
          panel: { value: 'var(--bg-panel)' },
          inverted: { value: 'var(--bg-inverted)' },
          error: { value: 'var(--bg-error)' },
          warning: { value: 'var(--bg-warning)' },
          success: { value: 'var(--bg-success)' },
          info: { value: 'var(--bg-info)' },
        },
        fg: {
          DEFAULT: { value: 'var(--fg)' },
          muted: { value: 'var(--fg-muted)' },
          subtle: { value: 'var(--fg-subtle)' },
          inverted: { value: 'var(--fg-inverted)' },
          error: { value: 'var(--fg-error)' },
          warning: { value: 'var(--fg-warning)' },
          success: { value: 'var(--fg-success)' },
          info: { value: 'var(--fg-info)' },
        },
        border: {
          DEFAULT: { value: 'var(--border)' },
          subtle: { value: 'var(--border-subtle)' },
          muted: { value: 'var(--border-muted)' },
          emphasized: { value: 'var(--border-emphasized)' },
          inverted: { value: 'var(--border-inverted)' },
          error: { value: 'var(--border-error)' },
          warning: { value: 'var(--border-warning)' },
          success: { value: 'var(--border-success)' },
          info: { value: 'var(--border-info)' },
        },
        panel: {
          bg: { value: 'var(--panelBg)' },
          font: { value: 'var(--panelFont)' },
          fontHighlight: { value: 'var(--panelFontHighlight)' },
          accent: { value: 'var(--panelAccent)' },
          text: { value: 'var(--panelText)' },
          textHover: { value: 'var(--panelTextHover)' },
        },
      },
    },
    recipes: {
      inputAddon: defineRecipe({
        base: {
          '.chakra-group:has(input:disabled) > &': { layerStyle: 'disabled' },
        },
      }),
    },
    slotRecipes: {
      drawer: defineSlotRecipe({
        slots: ['positioner'],
        base: {
          positioner: {
            '--drawer-z-index': 'var(--z-index-point-card, var(--chakra-z-index-popover))',
          },
        },
      }),
      checkbox: defineSlotRecipe({
        slots: ['root', 'label', 'control', 'indicator'],
        base: {
          root: {
            colorPalette: 'primary',
          },
        },
      }),
      numberInput: defineSlotRecipe({
        slots: [
          'root',
          'label',
          'input',
          'control',
          'valueText',
          'incrementTrigger',
          'decrementTrigger',
          'scrubber',
        ],
        base: {
          input: {
            borderRadius: 'md',
            _readOnly: { cursor: 'not-allowed' },
            _disabled: { bg: 'bg.muted' },
          },
        },
        variants: {
          variant: {
            outline: {
              input: { bg: 'white' },
            },
          },
        },
      }),
      datePicker: defineSlotRecipe({
        base: {
          root: {
            _disabled: { opacity: 1 },
          },
          input: {
            _disabled: { layerStyle: 'disabled', bg: 'bg.muted' },
          },
          trigger: {
            _disabled: { layerStyle: 'disabled' },
          },
        },
      }),
    },
  },
  globalCss: {
    '*': {
      fontFamily: 'var(--fontFamily)',
    },
    '[data-scope="date-picker"][data-part="table-cell-trigger"]': {
      '&[data-selected]': { background: 'var(--primary500)' },
    },
  },
});

export const system = createSystem(defaultConfig, volyConfig, pinpointConfig);
